import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { WebClipSummarySchema } from "@/domains/openapi/types";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";
import { Box } from "@radix-ui/themes";
import { useNavigate } from "react-router";
import { atlasRoutes } from "@/app/router/routes";

interface WebClipsTableProps {
  data: WebClipSummarySchema[];
  onViewWebClip: (id: string) => void;
  pageSize: number;
}

export const WebClipsTable: FC<WebClipsTableProps> = ({ data, onViewWebClip, pageSize }) => {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<WebClipSummarySchema>[]>(
    () => [
      {
        header: "ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Space Account",
        cell: ({ row }) => <UuidFieldRow id={row.original.associated_space_account_id} />,
      },
      {
        header: "Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.created_at} />,
      },
      {
        header: "Actions",
        cell: ({ row }) => (
          <Box style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button size="1" onClick={() => onViewWebClip(row.original.id)}>
              View
            </Button>
            {row.original.captured_website_id && (
              <Button
                size="1"
                variant="soft"
                onClick={() =>
                  navigate(
                    atlasRoutes.capturedWebsitesView({
                      params: { capturedWebsiteId: row.original.captured_website_id! },
                    }).path
                  )
                }
              >
                Website
              </Button>
            )}
          </Box>
        ),
      },
    ],
    [onViewWebClip, navigate]
  );

  const sortedData = useMemo(
    () => orderBy(data, x => DateTime.fromISO(x.created_at).toMillis(), "desc"),
    [data]
  );

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
